import React, { useEffect, useState } from 'react';
import axiosInstance from '../lib/axiosInstance';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    BarChart,
    Bar,
    Legend,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const CURRENCY_LOCALE = 'en-IN';
const CURRENCY_CODE = 'INR';

const formatCurrency = (value) =>
    new Intl.NumberFormat(CURRENCY_LOCALE, {
        style: 'currency',
        currency: CURRENCY_CODE,
        minimumFractionDigits: 2
    }).format(Number(value || 0));

function StatCard({ label, value, color = 'var(--text)', subLabel, badge, clickable, onClick }) {
    const interactiveClasses = clickable
        ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition transform'
        : '';

    return (
        <div
            className={`flex-1 min-w-[220px] card p-4 shadow relative overflow-hidden ${interactiveClasses}`}
            onClick={clickable ? onClick : undefined}
        >
            <div className="text-xs text-[var(--muted)] mb-1 flex items-center justify-between gap-2">
                <span>{label}</span>
                <div className="flex items-center gap-1">
                    {badge && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-500 border border-blue-500/30">
                            {badge}
                        </span>
                    )}
                    {clickable && (
                        <span className="text-[10px] text-[var(--muted)]">🔍</span>
                    )}
                </div>
            </div>
            <div className="text-2xl font-bold mt-0.5" style={{ color }}>{value}</div>
            {subLabel && <div className="text-[11px] text-[var(--muted)] mt-1">{subLabel}</div>}
        </div>
    );
}

function SkeletonCard() {
    return (
        <div className="flex-1 min-w-[220px] card p-4 shadow animate-pulse">
            <div className="h-3 w-20 bg-[var(--border)] rounded mb-3" />
            <div className="h-6 w-28 bg-[var(--border)] rounded mb-1" />
            <div className="h-3 w-16 bg-[var(--border)] rounded" />
        </div>
    );
}

const PERIOD_PRESETS = [
    { key: '7d', label: '7D' },
    { key: '30d', label: '30D' },
    { key: '90d', label: '90D' },
    { key: 'all', label: 'All Time' }
];

const PAYMENT_COLORS = {
    COD: '#f97316',
    PREPAID: '#0ea5e9'
};

const STATUS_COLORS = {
    delivered: '#22c55e',
    shipped: '#3b82f6',
    pending: '#f59e0b',
    returns: '#ef4444'
};

const BALANCE_COLORS = {
    available: '#16a34a',
    onHold: '#f97316',
    commission: '#6366f1'
};

export default function Stats() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [period, setPeriod] = useState('30d');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [dateError, setDateError] = useState('');

    const fetchAnalytics = async (periodParam = period, startParam = customStartDate, endParam = customEndDate) => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');
            const params = new URLSearchParams({ period: periodParam });
            if (periodParam === 'custom' && startParam && endParam) {
                params.append('startDate', startParam);
                params.append('endDate', endParam);
            }
            const response = await axiosInstance.get(`/brand/analytics?${params.toString()}`);
            if (response.data?.success) {
                setAnalytics(response.data.data);
                setSuccess('Analytics updated successfully.');
            } else {
                setError('Failed to load analytics');
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics(period, customStartDate, customEndDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, customStartDate, customEndDate]);

    const handlePeriodSelect = (selectedPeriod) => {
        setPeriod(selectedPeriod);
        setCustomStartDate('');
        setCustomEndDate('');
        setDateError('');
    };

    const handleCustomApply = () => {
        if (!customStartDate || !customEndDate) {
            setDateError('Please select both start and end dates for custom range');
            return;
        }
        if (new Date(customStartDate) > new Date(customEndDate)) {
            setDateError('Start date must be before end date');
            return;
        }
        setDateError('');
        setPeriod('custom');
    };

    const handleCustomClear = () => {
        setCustomStartDate('');
        setCustomEndDate('');
        setDateError('');
        setPeriod('30d');
    };

    const activeMeta = analytics?.meta;
    const activeStart = activeMeta?.startDate;
    const activeEnd = activeMeta?.endDate;

    const isActivePeriod = (p) => period === p && period !== 'custom';

    const summary = analytics?.summary;
    const timeSeries = analytics?.timeSeries || [];

    const hasData = !!(summary && (summary.totalOrders || timeSeries.length));

    const groupBy = analytics?.meta?.groupBy || 'day';

    const formatDateLabel = (dateStr) => {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        if (groupBy === 'week') {
            return `W/C ${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
        }
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    };

    const revenueSeries = timeSeries.map(p => ({
        date: p.date,
        revenue: p.revenue,
        cod: p.codAmount,
        prepaid: p.prepaidAmount
    }));

    const statusSeries = timeSeries.map(p => ({
        date: p.date,
        orders: p.orders,
        shipped: p.shipped,
        delivered: p.delivered,
        returns: p.returns
    }));

    const paymentSplitData = [
        { name: 'COD', value: summary ? summary.codAmount : 0 },
        { name: 'Prepaid', value: summary ? summary.prepaidAmount : 0 }
    ];

    const holdVsAvailableData = summary ? [
        { name: 'Net Available', value: summary.netAvailablePayment },
        { name: 'On Hold', value: summary.onHoldAmount },
        { name: 'Commission', value: summary.commissionAmount }
    ] : [];

    const onHoldBreakdown = summary?.onHoldBreakdown || {};

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div className="flex justify-between items-center mb-6 gap-4">
                <h1 className="lux-heading" style={{ fontSize: 28, fontWeight: 700 }}>Brand Analytics</h1>
                <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                    <span>Green = delivered / available</span>
                    <span>•</span>
                    <span>Amber = pending / on hold</span>
                    <span>•</span>
                    <span>Red = returns</span>
                    <span>•</span>
                    <span>Blue = shipped / commission</span>
                </div>
            </div>

            {/* Time Period Selector */}
            <div className="card p-4 mb-6 sticky top-0 z-10 bg-[var(--background)]/95 backdrop-blur">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex gap-2 flex-wrap">
                        {PERIOD_PRESETS.map(p => (
                            <button
                                key={p.key}
                            onClick={() => handlePeriodSelect(p.key)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                                    isActivePeriod(p.key)
                                        ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                                        : 'border-[var(--border)] text-[var(--muted)] hover:bg-[var(--surface)]'
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="h-8 w-px bg-[var(--border)] hidden md:block" />
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] text-[var(--muted)] font-semibold">Start Date</label>
                            <input
                                type="date"
                                value={customStartDate || ''}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="px-3 py-1.5 rounded border border-[var(--border)] bg-[var(--surface)] text-xs"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] text-[var(--muted)] font-semibold">End Date</label>
                            <input
                                type="date"
                                value={customEndDate || ''}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="px-3 py-1.5 rounded border border-[var(--border)] bg-[var(--surface)] text-xs"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCustomApply}
                                className="px-3 py-1.5 rounded bg-[var(--primary)] text-white text-xs font-semibold disabled:opacity-60"
                                disabled={loading}
                            >
                                {loading && period === 'custom' ? 'Loading...' : 'Apply'}
                            </button>
                            <button
                                onClick={handleCustomClear}
                                className="px-3 py-1.5 rounded border border-[var(--border)] bg-[var(--surface)] text-xs"
                                disabled={loading && period === 'custom'}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
                {dateError && (
                    <div className="mt-2 text-xs text-red-500">
                        {dateError}
                    </div>
                )}
                {activeStart && activeEnd && (
                    <div className="mt-2 text-xs text-[var(--muted)]">
                        Showing data from{' '}
                        {new Date(activeStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {' '}to{' '}
                        {new Date(activeEnd).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                )}
            </div>

            {error && (
                <div className="card p-4 mb-6 flex items-center justify-between gap-4" style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b' }}>
                    <span className="text-sm">{error}</span>
                    <button
                        onClick={() => fetchAnalytics()}
                        className="px-3 py-1.5 rounded bg-white text-xs font-semibold border border-[#991b1b]/40"
                    >
                        Retry
                    </button>
                </div>
            )}

            {success && !error && (
                <div className="fixed bottom-4 right-4 z-20 px-4 py-3 rounded-lg shadow-lg text-xs font-medium bg-emerald-500 text-white">
                    {success}
                </div>
            )}

            {loading && !analytics ? (
                <div>
                    <div className="flex gap-4 flex-wrap mb-4">
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <SkeletonCard key={idx} />
                        ))}
                    </div>
                    <div className="flex gap-4 flex-wrap mb-4">
                        {Array.from({ length: 4 }).map((_, idx) => (
                            <SkeletonCard key={idx} />
                        ))}
                    </div>
                    <div className="card p-8 text-center text-[var(--muted)]">
                        Loading charts...
                    </div>
                </div>
            ) : hasData ? (
                <>
                    {/* Top row: order stats */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        <StatCard
                            label="Total Orders"
                            value={summary?.totalOrders || 0}
                            color={STATUS_COLORS.delivered}
                        />
                        <StatCard
                            label="Shipped"
                            value={summary?.shipped || 0}
                            color={STATUS_COLORS.shipped}
                        />
                        <StatCard
                            label="Delivered"
                            value={summary?.delivered || 0}
                            color={STATUS_COLORS.delivered}
                        />
                        <StatCard
                            label="Pending"
                            value={summary?.pendingOrders || 0}
                            color={STATUS_COLORS.pending}
                        />
                        <StatCard
                            label="Returns"
                            value={summary?.returns || 0}
                            color={STATUS_COLORS.returns}
                        />
                        <StatCard
                            label="Replacements"
                            value={summary?.replacements || 0}
                            color={BALANCE_COLORS.commission}
                        />
                    </div>

                    {/* Financial stats row */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        <StatCard
                            label="COD Amount"
                            value={formatCurrency(summary?.codAmount || 0)}
                            color={PAYMENT_COLORS.COD}
                        />
                        <StatCard
                            label="Prepaid Amount"
                            value={formatCurrency(summary?.prepaidAmount || 0)}
                            color={PAYMENT_COLORS.PREPAID}
                        />
                        <StatCard
                            label="On Hold"
                            value={formatCurrency(summary?.onHoldAmount || 0)}
                            color={BALANCE_COLORS.onHold}
                            subLabel={
                                [
                                    {
                                        label: 'Awaiting Delivery',
                                        value: (onHoldBreakdown.shippedPendingDelivery || 0) + (onHoldBreakdown.preparingOrders || 0),
                                        color: STATUS_COLORS.shipped
                                    },
                                    {
                                        label: 'Within Return Window',
                                        value: onHoldBreakdown.deliveredWithinReturnWindow || 0,
                                        color: STATUS_COLORS.pending
                                    },
                                    {
                                        label: 'Replacement In Progress',
                                        value: summary?.onHoldBreakdown?.replacementInProgress || 0,
                                        color: BALANCE_COLORS.commission
                                    }
                                ]
                                    .filter(part => part.value > 0)
                                    .map(part =>
                                        `${part.label}: ${formatCurrency(part.value)}`
                                    )
                                    .join(' · ')
                            }
                        />
                        <StatCard
                            label="Deducted (Refunds)"
                            value={formatCurrency(summary?.deductedAmount || 0)}
                            color={STATUS_COLORS.returns}
                            subLabel={
                                [
                                    `Refunded/Returned: ${formatCurrency((summary?.deductedBreakdown?.refunded) || 0)}`
                                ].join(' · ')
                            }
                        />
                        <StatCard
                            label="Available Payment (Gross)"
                            value={formatCurrency(summary?.grossAvailablePayment || 0)}
                            color={BALANCE_COLORS.available}
                        />
                        <StatCard
                            label="Commission to Pay"
                            value={formatCurrency(summary?.commissionAmount || 0)}
                            color={BALANCE_COLORS.commission}
                            badge={
                                summary?.commissionPercentage != null
                                    ? `${summary.commissionPercentage}%`
                                    : 'Not Set'
                            }
                        />
                        <StatCard
                            label="Net Available Payment"
                            value={formatCurrency(summary?.netAvailablePayment || 0)}
                            color={BALANCE_COLORS.available}
                            subLabel="This is what you will actually receive"
                        />
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        {/* Revenue Over Time */}
                        <div className="card p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h2 className="text-sm font-semibold">Revenue Over Time</h2>
                                <span className="text-[11px] text-[var(--muted)]">
                                    Grouped by {analytics?.meta?.groupBy === 'week' ? 'week' : 'day'}
                                </span>
                            </div>
                            {revenueSeries.length === 0 ? (
                                <div className="text-xs text-[var(--muted)] text-center py-6">
                                    No data for the selected period.
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 260 }}>
                                    <ResponsiveContainer>
                                        <LineChart data={revenueSeries}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 10 }}
                                                tickFormatter={formatDateLabel}
                                            />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip
                                                formatter={(val) => formatCurrency(val)}
                                                labelFormatter={(label) => `Date: ${label}`}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="revenue"
                                                stroke="#22c55e"
                                                strokeWidth={2}
                                                dot={false}
                                                name="Revenue"
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* Order Status Breakdown */}
                        <div className="card p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h2 className="text-sm font-semibold">Order Status Breakdown</h2>
                            </div>
                            {statusSeries.length === 0 ? (
                                <div className="text-xs text-[var(--muted)] text-center py-6">
                                    No data for the selected period.
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 260 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={statusSeries}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 10 }}
                                                tickFormatter={formatDateLabel}
                                            />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="orders" stackId="a" fill="#6b7280" name="Orders" />
                                            <Bar dataKey="shipped" stackId="a" fill={STATUS_COLORS.shipped} name="Shipped" />
                                            <Bar dataKey="delivered" stackId="a" fill={STATUS_COLORS.delivered} name="Delivered" />
                                            <Bar dataKey="returns" stackId="a" fill={STATUS_COLORS.returns} name="Returns" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {/* Payment Method Split */}
                        <div className="card p-4 flex flex-col items-center">
                            <h2 className="text-sm font-semibold mb-3 self-start">Payment Method Split</h2>
                            {(!paymentSplitData[0].value && !paymentSplitData[1].value) ? (
                                <div className="text-xs text-[var(--muted)] text-center py-6 w-full">
                                    No payment data for selected period.
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 260 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={paymentSplitData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                                            >
                                                {paymentSplitData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={entry.name === 'COD' ? PAYMENT_COLORS.COD : PAYMENT_COLORS.PREPAID}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(val) => formatCurrency(val)} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* On Hold vs Available vs Commission */}
                        <div className="card p-4">
                            <h2 className="text-sm font-semibold mb-3">On Hold vs Available vs Commission</h2>
                            {holdVsAvailableData.every(d => !d.value) ? (
                                <div className="text-xs text-[var(--muted)] text-center py-6">
                                    No balance data for selected period.
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: 260 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={holdVsAvailableData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                            <YAxis tick={{ fontSize: 10 }} />
                                            <Tooltip formatter={(val) => formatCurrency(val)} />
                                            <Bar
                                                dataKey="value"
                                                name="Amount"
                                                radius={[4, 4, 0, 0]}
                                            >
                                                {holdVsAvailableData.map((entry, idx) => (
                                                    <Cell
                                                        key={`cell-b-${idx}`}
                                                        fill={
                                                            entry.name === 'Net Available'
                                                                ? BALANCE_COLORS.available
                                                                : entry.name === 'On Hold'
                                                                    ? BALANCE_COLORS.onHold
                                                                    : BALANCE_COLORS.commission
                                                        }
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Carried Forward summary */}
                    <div className="flex flex-wrap gap-4 mb-6">
                        <StatCard
                            label="Carried Forward"
                            value={formatCurrency(summary?.carriedForwardAmount || 0)}
                            color={BALANCE_COLORS.onHold}
                            subLabel="These orders were on hold in previous settlements and are now eligible for the current cycle."
                        />
                    </div>
                </>
            ) : (
                <div className="card p-8 text-center text-[var(--muted)]">
                    No analytics data available yet for the selected period.
                </div>
            )}
        </div>
    );
}
